/**
 * Backtest / calibration — phase P3 of the original brief, never run until now.
 *
 * This is the credibility gate. Everything else the Studio produces is a model
 * output; a backtest turns it into a model output WITH A MEASURED ERROR BAR, so
 * a board paper can say "£X uplift, against a model that reproduces last
 * season's actual revenue to within Y%" instead of asking anyone to take the
 * projection on faith.
 *
 * Method: replay the season's ACTUAL observed timetable through the engine, at
 * the observed fares, and reconcile predicted against actual revenue — whole
 * season, per month, and per service. Nothing here is fitted: the point is to
 * find out how wrong the model is, not to make it look right.
 *
 * Two honest caveats, both printed alongside the numbers:
 *   - The engine is driven by the same sales data it is being scored against,
 *     so this measures INTERNAL consistency (does the allocation, capacity and
 *     return logic reproduce the observed pattern?) rather than out-of-sample
 *     forecast accuracy. It catches structural error, not demand error.
 *   - It cannot validate Mode B at all. No observed passenger ever changed at
 *     Grosmont, so the interchange parameters are unfalsifiable from this data.
 */
import type {
  ClassDemand, Departure, EngineParams, Network, StudioPayload,
} from './types'
import { baselineDeps, runClasses } from './engine'
import { mkSlots, timedRunningDays } from './demand'
import { throughNetwork } from './network'

export interface BacktestSlice {
  key: string
  label: string
  yr: number
  month: number
  dayClass: string
  runningDays: number
  actual: number
  predicted: number
  actualPax: number
  predictedPax: number
  /** Signed error as a share of actual. */
  error: number
}

export interface BacktestServiceRow {
  direction: string
  time: string
  route: string
  actual: number
  predicted: number
  error: number
}

export interface BacktestResult {
  season: string
  slices: BacktestSlice[]
  services: BacktestServiceRow[]
  /** Mean absolute percentage error across slices, weighted by actual revenue. */
  mape: number
  /** Signed total bias: positive = the model over-predicts. */
  bias: number
  totalActual: number
  totalPredicted: number
  sliceCount: number
  caveats: string[]
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * Replay the observed timetable for every (month x day-class) in a season.
 *
 * `classes` are the V2 grain (Sat/Sun/Wkdy) so the comparison is against the
 * demand rows as recorded. Each slice runs on its OWN observed timetable — the
 * point is to reproduce what happened, not to score one timetable everywhere.
 */
export function runBacktest(
  payload: StudioPayload,
  params: EngineParams,
  season: 'current' | 'previous' = 'current',
  netOverride?: Network
): BacktestResult {
  const net = netOverride ?? throughNetwork()
  const demand = season === 'current' ? payload.demand : payload.prev_demand
  const label = (season === 'current' ? payload.season : payload.prev_season) ?? 'unknown'

  const keys = [...new Set(
    demand
      .filter((r) => ['Sat', 'Sun', 'Wkdy'].includes(r.day_class))
      .map((r) => `${r.yr}|${r.month_no}|${r.day_class}`)
  )].sort()

  const slices: BacktestSlice[] = []
  const svcAcc = new Map<string, BacktestServiceRow>()

  for (const k of keys) {
    const [yrS, moS, cls] = k.split('|')
    const yr = Number(yrS)
    const month = Number(moS)
    const rows = demand.filter((r) => r.yr === yr && r.month_no === month && r.day_class === cls)
    if (rows.length === 0) continue

    const runningDays = timedRunningDays(rows)
    if (!(runningDays > 0)) continue

    // D5 fix: pour each service over the SLICE's running days — a 3-day special
    // divided by 3 and replayed on all 21 modelled days was the +44% bias.
    const north = mkSlots(net, rows, 'Northbound', runningDays)
    const south = mkSlots(net, rows, 'Southbound', runningDays)
    if (north.length === 0 && south.length === 0) continue

    // The observed timetable, at the observed rake. 7 coaches is the V2 default
    // and the closest thing we have to "what actually ran" without an ERP feed.
    const deps: Departure[] = baselineDeps(net, north, south)
    const classes: ClassDemand[] = [{ cls, weight: 1, north, south }]
    const r = runClasses(net, classes, deps, params)

    // Actual = what the tills recorded for those services, per running day.
    const actual = rows.reduce((t, x) => t + x.net, 0) / runningDays
    const actualPax = rows.reduce((t, x) => t + x.pax, 0) / runningDays

    slices.push({
      key: k,
      label: `${MONTHS[month - 1]} ${yr} · ${cls}`,
      yr,
      month,
      dayClass: cls,
      runningDays,
      actual,
      predicted: r.revenue,
      actualPax,
      predictedPax: r.carried,
      error: actual > 0 ? (r.revenue - actual) / actual : 0,
    })

    // Per-service reconciliation: the level a planner argues at.
    for (const row of rows) {
      const sk = `${row.direction}|${row.dep_time}`
      const pred = r.perDep
        .filter((pd) => pd.direction === row.direction && pd.time === row.dep_time)
        .reduce((t, pd) => t + pd.outRevenue, 0)
      const cur = svcAcc.get(sk) ?? {
        direction: row.direction,
        time: row.dep_time,
        route: row.service ?? '',
        actual: 0,
        predicted: 0,
        error: 0,
      }
      cur.actual += row.net / runningDays
      cur.predicted += pred
      if (!cur.route && row.service) cur.route = row.service
      svcAcc.set(sk, cur)
    }
  }

  const totalActual = slices.reduce((t, s) => t + s.actual, 0)
  const totalPredicted = slices.reduce((t, s) => t + s.predicted, 0)
  // Revenue-weighted MAPE: a 40% miss on a £200 slice should not outweigh a 3%
  // miss on a £9,000 one.
  const mape = totalActual > 0
    ? slices.reduce((t, s) => t + Math.abs(s.predicted - s.actual), 0) / totalActual
    : 0
  const bias = totalActual > 0 ? (totalPredicted - totalActual) / totalActual : 0

  const services = [...svcAcc.values()]
    .map((s) => ({ ...s, error: s.actual > 0 ? (s.predicted - s.actual) / s.actual : 0 }))
    .sort((a, b) => Math.abs(b.predicted - b.actual) - Math.abs(a.predicted - a.actual))

  return {
    season: label,
    slices: slices.sort((a, b) => a.yr - b.yr || a.month - b.month || a.dayClass.localeCompare(b.dayClass)),
    services,
    mape,
    bias,
    totalActual,
    totalPredicted,
    sliceCount: slices.length,
    caveats: [
      'The engine is driven by the same sales data it is scored against, so this measures structural consistency (allocation, capacity, return logic) — not out-of-sample forecast accuracy.',
      'Rakes are assumed at the 7-coach default; without a per-service consist feed from ERP the true capacity on any given day is unknown, which will show up as error on the busiest services.',
      'Mode B cannot be validated here at all. No observed passenger changed at Grosmont, so the interchange parameters are unfalsifiable from this data and are quoted as a range.',
    ],
  }
}

/** A plain-English reading of the error, for the board paper. */
export function backtestVerdict(r: BacktestResult): { tone: 'ok' | 'warn' | 'error'; text: string } {
  const pct = (r.mape * 100).toFixed(1)
  const biasPct = (r.bias * 100).toFixed(1)
  const dir = r.bias >= 0 ? 'over' : 'under'
  if (r.sliceCount === 0) {
    return { tone: 'error', text: 'No slices could be replayed — there is not enough observed demand to calibrate against.' }
  }
  if (r.mape <= 0.1) {
    return {
      tone: 'ok',
      text: `The model reproduces observed revenue to within ${pct}% across ${r.sliceCount} month/day-type slices (${dir}-predicting by ${Math.abs(Number(biasPct))}% overall). Quote projections to one significant figure and carry this error as the band.`,
    }
  }
  if (r.mape <= 0.25) {
    return {
      tone: 'warn',
      text: `The model is within ${pct}% of observed revenue across ${r.sliceCount} slices, ${dir}-predicting by ${Math.abs(Number(biasPct))}% overall. Usable for ranking options; too coarse to defend a precise figure — present differences, not absolutes.`,
    }
  }
  return {
    tone: 'error',
    text: `The model is ${pct}% away from observed revenue across ${r.sliceCount} slices (${dir}-predicting by ${Math.abs(Number(biasPct))}%). Do not put an absolute £ figure in front of a board on this basis — investigate the worst services first.`,
  }
}
